import React from 'react';
import styled, { keyframes } from 'styled-components';
import SEO from '../components/SEO';
import BackgroundDesign from '../components/BackgroundDesign';
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Decoration = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  opacity: 0.03;
  filter: blur(80px);
  animation: ${floatAnimation} 6s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
  
  &.top-left {
    top: -100px;
    left: -100px;
  }
  
  &.bottom-right {
    bottom: -100px;
    right: -100px;
    animation-delay: -3s;
  }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

const WelcomeSection = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex ;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
     background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
              url(/images/photo_5802988364859820042_y.jpg) center/cover no-repeat;
    
    filter: brightness(0.3);
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
`;

const WelcomeContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeIn} 1s ease-out;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    
    span {
      color: ${props => props.theme.colors.primary};
    }
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    opacity: 0.9;
    margin-bottom: 2rem;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 30px;
  transform: translateX(-50%);
  z-index: 3;
  color: white;
  text-align: center;
  animation: ${floatAnimation} 2s ease-in-out infinite;
  
  .arrow {
    width: 20px;
    height: 20px;
    border-right: 3px solid white;
    border-bottom: 3px solid white;
    transform: rotate(45deg);
    margin: 0 auto;
  }
  
  .text {
    margin-top: 1rem;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 2px;
  }
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  animation: ${fadeIn} 0.8s ease-out;
  position: relative;
  z-index: 1;
`;

const FormSection = styled.section`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, transparent, ${props => props.theme.colors.primary}, transparent);
    border-radius: 2px;
  }
`;

const FormTitle = styled.h1`
  display: none; /* Remove the title to give more space to the form */
`;

const heartBeat = keyframes`
  0% { transform: scale(1); }
  15% { transform: scale(1.15); }
  30% { transform: scale(1); }
  45% { transform: scale(1.15); }
  60% { transform: scale(1); }
`;

const HeartDecoration = styled.div`
  position: absolute;
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.primary};
  transform: rotate(45deg);
  opacity: 0.1;
  z-index: 0;
  animation: ${heartBeat} 4s infinite;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 60px;
    background: ${props => props.theme.colors.primary};
    border-radius: 50%;
  }
  
  &::before {
    left: -30px;
  }
  
  &::after {
    top: -30px;
  }
  
  &.top-right {
    top: -20px;
    right: -20px;
  }
  
  &.bottom-left {
    bottom: -20px;
    left: -20px;
    animation-delay: 2s;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    
    z-index: -1;
  }
  
  iframe {
    width: 100%;
    height: 2000px;
    border: none;
    display: block;
    position: relative;
    z-index: 1;
    background: transparent;
  }
`;



const FormPage = () => {

  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="Join Us | Become a Member of The Big Heart Association Morocco"
        description="Join The Big Heart Association in Casablanca and become part of a community of changemakers. Volunteer, donate, or partner with us to transform lives across rural Morocco."
        url="/join"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Join Us", url: "/join" }]}
        noOrganizationSchema
      />

      <Decoration className="top-left" />
      <Decoration className="bottom-right" />
      <WelcomeSection>
        <WelcomeContent>
          <h1>Join <span>The Big Heart</span></h1>
          <p>Be part of our mission to empower communities and transform lives through education. Together, we can make a lasting impact where it matters most.</p>
        </WelcomeContent>
        <ScrollIndicator>
          <div className="arrow" />
          <div className="text">Scroll to Register</div>
        </ScrollIndicator>
      </WelcomeSection>
      <MainContent>
        <FormSection>
          <FormTitle>Join Our Community</FormTitle>
          <FormContainer>
            <HeartDecoration className="top-right" />
            <HeartDecoration className="bottom-left" />
            {/* <iframe
              src="https://docs.google.com/forms/d/e/1FAIpQLSebOfRorht-jJov-njgCP24f1dw7sEHeuEYhEBMGFsDE5z7XA/viewform?embedded=true"
              title="TBH Registration Form"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
            >
              Loading form...
            </iframe> */}
            Coming Soon ...
          </FormContainer>
        </FormSection>
      </MainContent>
    </PageWrapper>
  );
};

export default FormPage;
